import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { Component, Element, Prop, State } from '@stencil/core';
import store from '../../../../global/store';
import { tracker } from '../../../../global/tracker';

type IconParam = 'hover' | 'active';

@Component({
  tag: 'ui-bank-search-map',
  styleUrl: 'ui-bank-search-map.scss',
})
export class UiBankSearchMap {
  @Element() el;

  @Prop() zoom = 14;

  @State() map: google.maps.Map;

  @State() markers: google.maps.marker.AdvancedMarkerElement[] = [];

  @State() markerClusterer: MarkerClusterer;

  @State() selectedLocationMarker: google.maps.marker.AdvancedMarkerElement;

  onLocationChange() {
    if (store.state.selectedPlaceLocation) {
      this.map?.setCenter(store.state.selectedPlaceLocation);
      const mainMarker = document.createElement('div');
      mainMarker.innerHTML = `<sd-map-marker variant="main"></sd-map-marker>`;
      this.selectedLocationMarker =
        new google.maps.marker.AdvancedMarkerElement({
          position: new google.maps.LatLng(
            store.state.selectedPlaceLocation.lat(),
            store.state.selectedPlaceLocation.lng()
          ),
          content: mainMarker,
          map: this.map,
        });
    }
  }

  onResultsChange() {
    this.markerClusterer?.clearMarkers();
    this.markers.forEach((marker) => {
      // eslint-disable-next-line no-param-reassign
      marker.map = null;
    });
    this.markers = [];
    store.state.resultsVisible?.forEach((result) => {
      this.addBankMarker({
        latitude: parseFloat(result.coords.latitude),
        longitude: parseFloat(result.coords.longitude),
        iconimageUrl: result.institute.map_marker_logo_url,
        onClick: () => {
          store.state.selectedBank = result;
          store.state.isOverlayVisible = true;
          tracker.trackBankSelect('Button');
          tracker.trackInitialFormPage(result?.bankCode, result?.name);
        },
      });
    });

    if (store.state.resultsVisibleCount > 5) {
      this.markerClusterer = new MarkerClusterer({
        map: this.map,
        markers: this.markers,
        renderer: {
          render: ({ count, position }) => {
            const clusterMarker = document.createElement('div');
            clusterMarker.innerHTML = `<sd-map-marker variant="cluster">
            <span>${count}</span>
            <span class="sr-only">${count} Banken in diesem Bereich</span>
            </sd-map-marker>`;

            // create marker using svg icon
            return new google.maps.marker.AdvancedMarkerElement({
              position,
              content: clusterMarker,
              title: `Cluster of ${count} markers`,
              // adjust zIndex to be above other markers
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            });
          },
        },
      });
    }

    if (store.state.resultsVisible?.length) {
      this.zoomToEncompassMarkers();
    } else {
      this.map?.setCenter(store.state.selectedPlaceLocation);
      this.map?.setZoom(this.zoom);
    }
  }

  onHoveredResultChange() {
    this.removeAllMarkersIconParam('hover');
    if (typeof store.state.hoveredResultIndex === 'number') {
      const marker = this.markers[store.state.hoveredResultIndex];
      this.setMarkerIconParam(marker, 'hover');
    }
  }

  onSelectedResultChange() {
    this.removeAllMarkersIconParam('active');
    if (typeof store.state.selectedResultIndex === 'number') {
      const marker = this.markers[store.state.selectedResultIndex];
      this.setMarkerIconParam(marker, 'active');
    }
  }

  /**
   * remove previously appended ?hover and ?select
   * @param param ?hover | ?select
   */
  removeAllMarkersIconParam(param: IconParam) {
    this.markers.forEach((marker) => {
      const mapMarkerComponent = (marker.content as any)
        .firstElementChild as HTMLUnknownElement;
      if (mapMarkerComponent.getAttribute('state') === param) {
        // remove hovered/active state
        mapMarkerComponent.setAttribute('state', 'default');
      }
    });
  }

  /**
   * append param to make marker selectable and stylable via css
   * @param param ?hover | ?select
   */
  setMarkerIconParam(
    marker: google.maps.marker.AdvancedMarkerElement,
    param: IconParam
  ) {
    const mapMarkerComponent = (marker.content as any)
      .firstElementChild as HTMLUnknownElement;
    // Bounce if list element is triggered
    mapMarkerComponent.setAttribute('animate', '');
    // Do not set hover state if marker is already selected
    if (mapMarkerComponent.getAttribute('state') === 'active') return;
    // Show element as hovered or selected if triggered from the side
    mapMarkerComponent.setAttribute('state', param);
  }

  zoomToEncompassMarkers() {
    const bounds = new google.maps.LatLngBounds();

    // eslint-disable-next-line no-plusplus
    this.markers.forEach((marker) => {
      bounds.extend(marker.position);
    });

    this.map?.fitBounds(bounds);
    this.map?.setZoom(this.map.getZoom() - 1);
  }

  addMarker(
    latitude: number,
    longitude: number,
    iconimageUrl: string
  ): google.maps.marker.AdvancedMarkerElement {
    const placeMarker = document.createElement('div');
    placeMarker.innerHTML = iconimageUrl
      ? `<sd-map-marker  state="default" variant="place">
          <img
            alt="Logo der Bank" 
            style="width: 30px;"
            src="${iconimageUrl}"
          />
          <span class="sr-only">Anfrage an Bank senden</span>
        </sd-map-marker>`
      : `<sd-map-marker state="default" variant="place">
          <sd-icon aria-hidden="true" name="union-investment/content/bank"></sd-icon>
          <span class="sr-only">Anfrage an Bank senden</span>
          </sd-map-marker>`;

    const myLatlng = new google.maps.LatLng(latitude, longitude);
    return new google.maps.marker.AdvancedMarkerElement({
      position: myLatlng,
      content: placeMarker,
      map: this.map,
    });
  }

  addBankMarker({ latitude, longitude, iconimageUrl, onClick }) {
    const marker = this.addMarker(latitude, longitude, iconimageUrl);
    this.markers.push(marker);
    marker.addListener('click', () => {
      store.state.selectedResultIndex = this.markers.indexOf(marker);
      onClick?.();
    });
    marker.addListener('mouseover', () => {
      store.state.hoveredResultIndex = this.markers.indexOf(marker);
    });
    marker.addListener('mouseout', () => {
      store.state.hoveredResultIndex = null;
    });
  }

  componentWillLoad() {
    store.onChange('hoveredResultIndex', () => {
      this.onHoveredResultChange();
    });
    store.onChange('selectedResultIndex', () => {
      this.onSelectedResultChange();
    });
    store.onChange('resultsVisible', () => {
      this.onResultsChange();
    });
    store.onChange('selectedPlaceLocation', () => {
      this.onLocationChange();
    });

    this.map = new google.maps.Map(this.el, {
      mapId: 'BANK-SEARCH-MAP',
      center: store.state.selectedPlaceLocation,
      zoom: this.zoom,
      mapTypeControl: false,
      zoomControl: true,
      streetViewControl: false,
    });
    this.map.addListener('dragstart', () => {
      store.state.isSliderCondensed = true;
    });
    this.onLocationChange();
  }
}
